import { EXIT_BLOCK_CAP, EXIT_BLOCK_TYPES, type ExitBlock, type ExitContent } from '@agavi/blocks';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronUp, GripVertical, Redo2, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnchoredStylingBar } from './anchored-styling-bar';
import { AtExitProvider } from './answer-ref-context';
import { useArmedDelete } from './block-actions';
import { cardBorderClass } from './block-card';
import { BLOCK_META } from './block-library';
import { DiscardConfirmModal } from './discard-confirm-modal';
import { dndA11y } from './dnd-a11y';
import { pulseElement } from './field-reveal';
import { stripHtmlForLabel } from './formula-plaintext';
import ImageBlock from './image-block';
import { QuickAddBar } from './quick-add-bar';
import { RichTextEditor } from './rich-text-editor';
import SoundBlock from './sound-block';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useIsPhoneLandscape } from './use-viewport';

import type { Block } from '@/types/experience';
import type { Editor } from '@tiptap/core';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';

/**
 * WHAT A STUDENT READS ON THEIR WAY OUT, authored.
 *
 * A stacked list of blocks, which is the language the task builder already speaks — not a layout
 * model, not a picker, not a second canvas. Teachers read it without being taught it, and it is
 * what keeps this surface small enough to be worth having.
 *
 * ## Three types, and the reason is not taste
 *
 * An INPUT block here is impossible rather than merely unwanted: the exit shows because the flow
 * has ALREADY routed, so an answer could never be graded and could never route. Dataviz is
 * excluded for a softer reason — a static authored chart on an exit is decoration, and the chart
 * worth having would plot the student's own answers, which needs attempt data in a series.
 * `EXIT_BLOCK_TYPES` carries the rule, and `ExitBlock`'s own type carries it again.
 *
 * ## It is the canvas, in miniature
 *
 * Every gesture here is the one the task builder already uses for a list of blocks, because a
 * teacher arrives having learned them: a reorder rail on the LEFT (up, grip, down), a bin on the
 * right that arms on the first press and deletes on the second, and the dashed quick-add strip
 * at the foot. The first spelling of this dialog invented its own arrangement for each of those
 * — a column of three buttons on the right, a single-press delete, and a row of pill buttons —
 * and every one of them was a second way to do something the canvas had already settled.
 *
 * ## ONE TOOLBAR, PINNED
 *
 * Every rich-text row shares the bar at the top, which is the house pattern for a row editor:
 * `RichTextEditor` with `hideToolbar`, handing its editor to one `useSharedEditorToolbar` on
 * focus. The first spelling rendered a whole `TextBlock` per row, so each one carried its own
 * twelve-button grid, its own name header and a `+` for a version bank an exit cannot have —
 * about 100px of chrome before any writing, repeated per block.
 *
 * Image and sound render their real components in `condensed` form: their bulk is a picker
 * rather than a toolbar, and those components are what insist on alt text and a transcript.
 *
 * ## Undo, as the reaction editor does it
 *
 * The modal keeps its own state while it is open and commits ONCE, so an undo out in the builder
 * takes the whole exit back rather than unpicking it a word at a time. Closing with changes asks
 * before discarding — the same `DiscardConfirmModal` that editor uses, for the same reason: the
 * work only exists here until it is committed.
 */

/**
 * THE MODAL'S OWN UNDO.
 *
 * The dialog commits once, so while it is open the builder's history has nothing to undo — an
 * exit under construction does not exist out there yet. Without a history of its own, a teacher
 * who deleted the wrong row had no way back at all, which is worse than the surface it replaced.
 *
 * A snapshot stack over the block list, not a log of operations: five blocks of plain data are
 * cheap to copy, and a snapshot cannot describe a change it does not actually make — the class
 * of bug an operation log invites. `RichTextEditor` already re-syncs when its `content` prop
 * changes from underneath it (it was built for exactly this, for the task's own history), so a
 * restored snapshot shows in the editors rather than only in the data.
 *
 * ## The grain, which is a choice
 *
 * `key` coalesces: a run of edits carrying the same key collapses into ONE step. Structural
 * edits pass `null` and so are always their own step; typing in a row passes `text:<id>`, so a
 * sentence typed into one block undoes as a sentence. Finer than that belongs to the editor the
 * caret is in — tiptap keeps its own character-level history and Ctrl+Z inside a focused editor
 * reaches it — and this button is the coarse tool beside it.
 *
 * `lastKey` resets after an undo or a redo, so the next keystroke starts a fresh step instead of
 * folding into the one just restored.
 */
interface ExitHistory {
  blocks: ExitBlock[];
  past: ExitBlock[][];
  future: ExitBlock[][];
  lastKey: string | null;
}

type ExitHistoryAction =
  | { type: 'edit'; update: (bs: ExitBlock[]) => ExitBlock[]; key: string | null }
  | { type: 'undo' }
  | { type: 'redo' };

/** Deep enough that a dialog session never reaches it, bounded so a stuck key cannot grow it. */
const HISTORY_DEPTH = 100;

/**
 * Does this text row say anything?
 *
 * A tiptap document that has been focused and left alone is `<p></p>`, not an empty string, so
 * a truthiness check calls it written. Tags stripped and whitespace (including the `&nbsp;` an
 * editor leaves behind) collapsed, which is the same test the issue census makes of a question.
 */
export function hasExitText(html: unknown): boolean {
  return typeof html === 'string'
    && html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
}

export function exitHistoryReducer(s: ExitHistory, a: ExitHistoryAction): ExitHistory {
  if (a.type === 'edit') {
    const blocks = a.update(s.blocks);
    // Reference equality is the signal a handler declined the edit — `move` past the ends, an
    // add at the cap. A no-op must not spend an undo step.
    if (blocks === s.blocks) return s;
    const coalesce = a.key !== null && a.key === s.lastKey;
    const past = coalesce ? s.past : [...s.past, s.blocks].slice(-HISTORY_DEPTH);
    return { blocks, past, future: [], lastKey: a.key };
  }
  if (a.type === 'undo') {
    const prev = s.past[s.past.length - 1];
    if (prev === undefined) return s;
    return { blocks: prev, past: s.past.slice(0, -1), future: [s.blocks, ...s.future], lastKey: null };
  }
  const next = s.future[0];
  if (next === undefined) return s;
  return { blocks: next, past: [...s.past, s.blocks], future: s.future.slice(1), lastKey: null };
}

export function ExitContentModal({ content, slotTitle, experienceId, taskId, focusBlockId, onCommit, onClose }: {
  content: ExitContent | undefined;
  /** The event this exit belongs to, so the dialog says WHICH exit is being written. */
  slotTitle: string;
  experienceId: string;
  taskId: string;
  /**
   * The block a teacher arrived here to fix, scrolled to and flashed on open.
   *
   * Set only by the board's problems panel. An exit holds up to five blocks and the row that sent
   * them named one of them; opening on the list and leaving them to work out which is the half of
   * the arrival the rest of the app does not make them do.
   */
  focusBlockId?: string;
  onCommit: (next: ExitContent | undefined) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toolbar = useSharedEditorToolbar();
  /* A sideways phone, which is a layout and not a width — see `useIsPhoneLandscape`. */
  const landscape = useIsPhoneLandscape();
  /*
   * A DIALOG FOR WRITING OPENS WITH SOMEWHERE TO WRITE.
   *
   * An empty exit opened on an empty list, so the first thing every teacher did was press Add
   * Text — a step with no decision in it, since text is what an exit almost always is. Seeded
   * as the BASELINE rather than as an edit, so an untouched dialog is not dirty and Cancel does
   * not ask about discarding a block nobody added.
   *
   * It is why `commit` drops empty text: a seeded row the teacher never filled must not be
   * saved as content, or every exit anyone opened would read as authored on the event card.
   */
  const initial = useRef<ExitBlock[]>(
    content?.blocks?.length ? content.blocks : [{ id: `exb_${crypto.randomUUID()}`, type: 'text', data: {} }],
  );
  const [history, dispatch] = useReducer(exitHistoryReducer, {
    blocks: initial.current, past: [], future: [], lastKey: null,
  });
  const blocks = history.blocks;
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  /*
   * WHERE THE SHELL SHOULD PUT THE CARET, which is not where it would guess.
   *
   * `ModalShell` focuses the first control that is not the close button, and this dialog's
   * first control is Undo in the title bar. The row's own `commands.focus()` ran first and the
   * shell's pass — a rAF later — took it straight back, so a dialog that seeds a row to type in
   * opened with the caret on an undo button. Naming the editor here is what the shell's
   * `initialFocusRef` is for.
   */
  const firstEditor = useRef<HTMLElement | null>(null);

  const undo = () => dispatch({ type: 'undo' });
  const redo = () => dispatch({ type: 'redo' });
  const edit = (update: (bs: ExitBlock[]) => ExitBlock[], key: string | null = null) =>
    dispatch({ type: 'edit', update, key });

  /*
   * THE CANVAS'S OWN ACTIVATION DISTANCES, which these claimed to be and were not.
   *
   * The canvas asks for 15px of mouse travel and a 100ms touch hold with 12px of tolerance;
   * this had 4px and 180ms/6px. Four pixels is inside the slop of an ordinary click, so in a
   * dialog that is nothing but text editors and media, a press that meant "put the caret here"
   * or "select this word" became a drag — the row lifts out of the flow mid-gesture, which is
   * what makes the list look like it collapses. Reported as "the cards gets smushed and i can
   * never drop them after picking them up ... match how we've done drag-and-drop in the main
   * task builder".
   *
   * `collisionDetection` is `closestCenter`, which is what the canvas's own function reduces to
   * for a row drag — its only branch is for a block coming from the library, and this dialog
   * has no library.
   */
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
  );

  /*
   * AND THE CANVAS'S DRAG OVERLAY, which this had none of.
   *
   * Without one the row being dragged is the row in the list, translated — so its editors, its
   * image and its sound player are all still live under the pointer, re-laying-out as it moves
   * and dragging their own measured height around with them. The canvas lifts a ghost out
   * instead and leaves the original dimmed in place, which is what keeps every row's box still
   * while the drop target is being worked out.
   *
   * A COMPACT ghost rather than the row itself: mounting a second copy of a text row means a
   * second TipTap editor that would register itself with the shared toolbar — the bar follows
   * whichever editor spoke last — and a second sound player. The canvas already draws its
   * library drag this way, so the shape is not a new one.
   */
  const [dragging, setDragging] = useState<ExitBlock | null>(null);

  /* Compared against the snapshot taken at open, so adding a block and deleting it again is not
     a change — the same test the reaction editor makes before it guards its close. */
  const isDirty = JSON.stringify(blocks) !== JSON.stringify(initial.current);
  const requestClose = () => (isDirty ? setConfirmDiscard(true) : onClose());

  /*
   * ONE COMMIT, and an empty list commits `undefined` rather than an empty husk — the slot's key
   * disappears instead of persisting a shape that renders nothing. The loader drops such a husk
   * on the next load anyway; not writing one is the honest version.
   */
  function commit() {
    /*
     * EMPTY TEXT IS NOT CONTENT. A block with nothing in it renders nothing for the student, so
     * storing one is storing a husk — the same argument the empty-list rule below already makes,
     * one level down. It matters more now that the dialog seeds a row: without this, opening an
     * exit and closing it with Done would file an "authored" exit that shows nothing.
     *
     * Only TEXT, and only when genuinely blank. An image with no asset chosen yet is a row the
     * teacher put there on purpose and will come back to; dropping it would lose that intent.
     */
    const written = blocks.filter((b) => b.type !== 'text' || hasExitText(b.data.html));
    onCommit(written.length > 0 ? { blocks: written } : undefined);
    onClose();
  }

  const atCap = blocks.length >= EXIT_BLOCK_CAP;

  function add(type: string) {
    if (atCap) return;
    edit((bs) => [...bs, { id: `exb_${crypto.randomUUID()}`, type: type as ExitBlock['type'], data: {} }]);
  }

  /*
   * KEYED BY BLOCK, so a run of keystrokes in one row is one undo step and moving to another row
   * starts a new one. Everything a media block writes — an asset, alt text, a caption — shares
   * its row's key for the same reason: they are one teacher setting up one block.
   */
  function update(id: string, data: Record<string, unknown>) {
    edit(
      (bs) => bs.map((b) => (b.id === id ? { ...b, data: { ...b.data, ...data } } : b)),
      `edit:${id}`,
    );
  }

  /*
   * ORDER IS CONTENT HERE: text / image / text is a different summary from image / text / text,
   * and without this a teacher who adds them in the wrong order can only delete and retype.
   *
   * Drag AND arrows, which is not belt-and-braces — it is what the canvas does, and for the
   * reason the canvas does it. The grip is `aria-hidden` and untabbable, so the arrows are the
   * whole keyboard story; a pointer gets the gesture it expects.
   */
  function move(index: number, delta: -1 | 1) {
    const to = index + delta;
    edit((bs) => {
      // A backstop: the buttons are disabled at the ends, so this cannot be reached from the
      // UI and no test exercises it. Kept because what it prevents is a swap with `undefined`.
      if (to < 0 || to >= bs.length) return bs;
      const next = [...bs];
      [next[index], next[to]] = [next[to]!, next[index]!];
      return next;
    });
  }

  function handleDragStart({ active }: DragStartEvent) {
    setDragging(blocks.find((b) => b.id === active.id) ?? null);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setDragging(null);
    if (!over || active.id === over.id) return;
    edit((bs) => {
      const from = bs.findIndex((b) => b.id === active.id);
      const to = bs.findIndex((b) => b.id === over.id);
      return from < 0 || to < 0 ? bs : arrayMove(bs, from, to);
    });
  }

  /*
   * WHILE THIS IS OPEN IT OWNS UNDO.
   *
   * The task page has a document-level Ctrl+Z that drives the BUILDER's history. It skips an
   * event whose target is editable, so typing in a row was always safe — but with focus on a
   * button in this dialog (a bin, a move arrow, Done) the keystroke reached past the modal and
   * undid something in the task behind it, which the teacher cannot even see. The same hazard
   * the phone block session guards against, and the same answer: the inner surface takes it.
   *
   * CAPTURE on `document`, which is what makes it the inner surface: capture runs outer-in and
   * reaches here before the page's bubble-phase listener, so `stopPropagation` keeps the event.
   * An editable target is handed through untouched — that keystroke belongs to tiptap's own
   * character-level history, which is the finer tool and the one a caret expects.
   */
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!e.ctrlKey && !e.metaKey) return;
      const target = e.target as HTMLElement | null;
      /* `closest` as well as `isContentEditable`: the property is the browser's answer and the
         attribute is the DOM's, and they are not the same test. A keystroke can be targeted at a
         node INSIDE the editable rather than at it, and jsdom does not implement the property at
         all — so a check written on the property alone passes in a browser and swallows the
         keystroke under test. */
      if (target?.isContentEditable
        || target?.closest('[contenteditable="true"]')
        || target?.tagName === 'INPUT'
        || target?.tagName === 'TEXTAREA') return;
      const key = e.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      e.preventDefault();
      e.stopPropagation();
      if (key === 'y' || e.shiftKey) redo(); else undo();
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  return (
    <ModalShell
      layout='editor'
      title={t('task.flow.exit.modalTitle', { event: slotTitle })}
      onClose={requestClose}
      initialFocusRef={firstEditor}
      /* The corner the two formula editors already put these in, in the same order. */
      headerActions={
        <>
          <IconButton
            type='button'
            size='sm'
            disabled={history.past.length === 0}
            /* The press must not take focus off the editor it is about to act on — the same
               reason both formula editors guard their undo this way. */
            onMouseDown={(e) => e.preventDefault()}
            onClick={undo}
            aria-label={t('common.undo')}
            title={t('common.undo')}
          >
            <Undo2 aria-hidden='true' className='h-4 w-4' />
          </IconButton>
          <IconButton
            type='button'
            size='sm'
            disabled={history.future.length === 0}
            onMouseDown={(e) => e.preventDefault()}
            onClick={redo}
            aria-label={t('common.redo')}
            title={t('common.redo')}
          >
            <Redo2 aria-hidden='true' className='h-4 w-4' />
          </IconButton>
        </>
      }
      /* PINNED, and outside the scroll rather than sticky inside it — see `subheader`. Sticky
         left a few pixels of the list sliding visibly above the bar, because a scrollport's top
         edge is the body's padding edge and `top-0` pins below it.

         `px-4 py-2` is the band's own padding, matching the formatting row both formula editors
         put in exactly this position. */
      /* INSIDE `AtExitProvider` as well as the rows are. The bar is a slot of the shell, so it
         is not a child of anything this component wraps — and it is the bar that renders the
         recall picker for every row, since the rows run `hideToolbar`. Wrapped only around the
         rows, the picker sat outside the provider and refused this task's own answers in the
         one place they are available.

         ABSENT IN LANDSCAPE, where it is a rail beside the list rather than a band above it —
         see the body below. A phone on its side is 390px tall and two rows of buttons spend a
         quarter of that before any writing. */
      subheader={landscape ? undefined : (
        <AtExitProvider>
          <AnchoredStylingBar toolbar={toolbar} className='px-4 py-2' />
        </AtExitProvider>
      )}
      /*
       * TWO BANDS AT THE FOOT, in the order they are reached: what you can add, then what you
       * do with what you added.
       *
       * Below the scroll rather than at the end of it. The list grows and the dialog does not,
       * so a quick-add bar inside the body would walk down and off the bottom as blocks were
       * added — the affordance for adding a sixth block hidden by the five already there.
       */
      footer={
        <>
          <div className='border-t border-ws-border px-4 py-2'>
            {/* The canvas's bar, bare: in a banded dialog the rule above it is the separation,
                and the dashed outline it wears on the canvas was a second one drawn inside the
                first. Same columns, same labels, same behaviour. */}
            <QuickAddBar
              bare
              types={[...EXIT_BLOCK_TYPES]}
              onAdd={add}
              disabled={atCap}
              describedBy={atCap ? 'exit-cap-note' : undefined}
            />
          </div>
          {/* The action band: a muted status line on the left and the two controls on the right,
              which is the footer both formula editors already have. The cap note IS that status
              line rather than a stray caption under the bar. */}
          <div className='flex shrink-0 items-center justify-between gap-3 border-t border-ws-border px-4 py-3'>
            <span id='exit-cap-note' className='min-w-0 truncate text-xs text-ws-muted'>
              {atCap ? t('task.flow.exit.capReached', { max: EXIT_BLOCK_CAP }) : ''}
            </span>
            <div className='flex shrink-0 items-center gap-2'>
              <Button variant='secondary' size='sm' onClick={requestClose}>{t('common.cancel')}</Button>
              <Button variant='primary' size='sm' onClick={commit}>{t('common.done')}</Button>
            </div>
          </div>
        </>
      }
    >
      {/* THE WHOLE DIALOG IS THE EXIT, which is what lets a recall quote this task's own
          answers: by the time an exit is read, every question in it has been answered. A
          block editor provides no such thing and so refuses them — see `AtExitContext`. */}
      <AtExitProvider>
      {/* A ROW in landscape — toolbar rail, then the list — and a plain band everywhere else.
          The same arrangement `InputBlockFrame` uses on a sideways phone, so a teacher meets
          one layout rather than two. `contents` keeps the portrait case exactly as it was:
          the wrapper contributes no box and the band below is the body's only child. */}
      <div className={landscape ? 'flex h-full min-h-0' : 'contents'}>
      {landscape && <AnchoredStylingBar toolbar={toolbar} vertical />}
      <div className={landscape ? 'min-w-0 flex-1 overflow-y-auto px-3 py-2' : 'px-4 py-3'}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setDragging(null)}
          accessibility={dndA11y(t, blocks.map((b) => b.id), 'item')}
        >
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <div className='flex flex-col gap-3'>
              {blocks.map((block, index) => (
                <ExitBlockRow
                  key={block.id}
                  block={block}
                  experienceId={experienceId}
                  taskId={taskId}
                  toolbar={toolbar}
                  /* The FIRST text row takes focus when the dialog opens. A dialog whose whole
                     job is writing should not open with a greyed-out toolbar and nowhere to type
                     -- and focusing an editor is what lights the bar. Only the first, and only
                     text: an image row has nothing to put a caret in. */
                  focusOnOpen={index === blocks.findIndex((b) => b.type === 'text')}
                  focusRef={firstEditor}
                  flash={block.id === focusBlockId}
                  position={{ index, total: blocks.length }}
                  onMove={(delta) => move(index, delta)}
                  onUpdate={(data) => update(block.id, data)}
                  onRemove={() => edit((bs) => bs.filter((b) => b.id !== block.id))}
                />
              ))}
            </div>
          </SortableContext>
          {/* `dropAnimation={null}`, as on the canvas: the list has already moved by the time the
              ghost would fly home, so the animation plays against a layout that changed. */}
          <DragOverlay dropAnimation={null}>
            {dragging && <ExitDragGhost block={dragging} />}
          </DragOverlay>
        </DndContext>

      </div>
      </div>
      </AtExitProvider>

      {confirmDiscard && (
        <DiscardConfirmModal
          onDiscard={() => { setConfirmDiscard(false); onClose(); }}
          onKeepEditing={() => setConfirmDiscard(false)}
        />
      )}
    </ModalShell>
  );
}

/**
 * THE GHOST UNDER THE POINTER while a row is being dragged.
 *
 * Deliberately not the row. A second copy of a text row is a second TipTap editor, and the
 * dialog's toolbar binds to whichever editor spoke last — so a ghost that mounted one would
 * steal the bar from the row the teacher was actually writing in. A second sound row would mount
 * a second player. The canvas draws its library drag as a glyph and a word for the same reason,
 * so this is the established shape rather than a new one.
 *
 * It says WHICH row is in the air: the block's own library glyph, and for a text row the first
 * words of what it holds — three identical "Text" cards would answer nothing.
 */
function ExitDragGhost({ block }: { block: ExitBlock }) {
  const { t } = useTranslation();
  const meta = BLOCK_META[block.type];
  const Icon = meta?.Icon;
  const words = block.type === 'text'
    ? stripHtmlForLabel((block.data.html as string) ?? '').trim() : '';
  return (
    <div className='pointer-events-none flex items-center gap-2 rounded-lg border border-ws-btn-active-fg bg-ws-elevated px-3 py-2 text-ws-btn-active-fg shadow-xl'>
      {Icon && <Icon className='h-4 w-4 shrink-0' />}
      <span className='max-w-56 truncate text-xs'>
        {words || (meta ? t(meta.labelKey) : block.type)}
      </span>
    </div>
  );
}

/**
 * One row: reorder rail, the block, the bin — the canvas's arrangement at a smaller size.
 *
 * TEXT is the common case and gets the light treatment: a bare `RichTextEditor` with
 * `hideToolbar`, handing its editor to the modal's one toolbar on focus — the house pattern for
 * a row editor, and the thing a whole `TextBlock` per row was costing 100px each to avoid.
 *
 * No `placeholder`: the owner's standing rule is that inputs spawn empty with no ghost text, and
 * the editor falls back to its own accessible name without one.
 *
 * IMAGE AND SOUND keep their real components, in `condensed` form. Their bulk is a picker rather
 * than a toolbar, and those components are what insist on alt text and a transcript —
 * hand-rolling the two would quietly drop both, which is the one thing a summary screen cannot
 * afford to lose. Condensed strips the three things a row already provides or cannot use: the
 * title header, the editor's page padding, and a scenario bank an exit has no stepper for.
 *
 * ONE BIN FOR ALL THREE, now that the media blocks arrive without their own. It arms on the
 * first press and deletes on the second, which is how every block on the canvas is deleted —
 * `useArmedDelete` is the same hook they use, so the outside-click disarm cannot drift.
 */
function ExitBlockRow({ block, experienceId, taskId, toolbar, focusOnOpen, focusRef, flash, position, onMove, onUpdate, onRemove }: {
  block: ExitBlock;
  experienceId: string;
  taskId: string;
  toolbar: ReturnType<typeof useSharedEditorToolbar>;
  /** Named for what it does rather than `autoFocus`, which is a barred DOM prop. */
  focusOnOpen: boolean;
  /** Where to publish this row's editor element, when it is the one the dialog opens on. */
  focusRef: React.MutableRefObject<HTMLElement | null>;
  /** This is the row a problems-panel trip was about: bring it into view and ring it once. */
  flash: boolean;
  position: { index: number; total: number };
  onMove: (delta: -1 | 1) => void;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const editorRef = useRef<Editor | null>(null);
  const { blockRef, isDeleteArmed, triggerDelete, disarmDelete } = useArmedDelete(onRemove, () => {});
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });

  /*
   * Once, on open. `blockRef` is already on the row's outer element for the armed-delete guard,
   * which is exactly the element to ring — the frame, not the editor inside it. A frame later,
   * because the dialog is still being laid out on mount and a scroll computed against a box that
   * has not settled lands in the wrong place.
   */
  useEffect(() => {
    if (!flash) return;
    const bring = () => {
      const el = (blockRef as React.MutableRefObject<HTMLDivElement | null>).current;
      /* CENTRED, not the nearest edge: the dialog's scroller is ~440px and these rows are tall,
         so a minimal scroll leaves one in view by the measurement and cut in half to read. */
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return el;
    };
    const first = requestAnimationFrame(() => { const el = bring(); if (el) pulseElement(el); });
    /*
     * And AGAIN once the media has laid out. An image row is a few pixels tall until its asset
     * arrives and a sound row until its player mounts, so the first pass measures a document that
     * is about to grow underneath it — which is exactly how the sound block, the last row and the
     * one most often flagged, ended up back under the Add bar after a scroll that had reached it.
     */
    const settle = window.setTimeout(bring, 400);
    return () => { cancelAnimationFrame(first); clearTimeout(settle); };
  }, [flash, blockRef]);

  const register = (e: Editor | null) => {
    if (!e) return;
    toolbar.activeEditorRef.current = e;
    toolbar.setActiveEditor(e);
  };

  /* The task's `Block` carries fields an exit block has no use for (`order`, the feedback a
     display block never had). Widening `ExitBlock` to match would store them; filling them here
     does not. `embedded` opens the component as an editor and suppresses its own phone portal,
     which would otherwise be a second overlay inside this dialog. */
  const asBlock = { ...block, order: 0 } as unknown as Block;
  const shared = {
    isPreview: false,
    embedded: true,
    condensed: true,
    onUpdate,
    onRemove,
    // An exit holds at most five blocks, so duplicating one is a button that mostly cannot be
    // pressed. Add is the affordance; there is nothing for this to do.
    onDuplicate: () => {},
  };

  const body = block.type === 'text' ? (
    <div className='p-2'>
      <RichTextEditor
        content={(block.data.html as string) ?? ''}
        onChange={(html) => onUpdate({ html })}
        editorClassName='min-h-6'
        hideToolbar
        onEditorReady={(e) => {
          editorRef.current = e;
          // Named for the shell's focus pass, which runs a frame later and would otherwise land
          // on the title bar's Undo — see `firstEditor`.
          if (focusOnOpen) focusRef.current = e.view.dom as HTMLElement;
          // Through the editor rather than a DOM `autoFocus`, which is barred for the usual
          // reasons — and this is a dialog whose whole job is writing, so opening it with a
          // greyed bar and nowhere to type is the thing being fixed. Focusing lights the bar.
          if (focusOnOpen) e.commands.focus();
        }}
        onEditorFocus={() => register(editorRef.current)}
      />
    </div>
  ) : block.type === 'image' ? (
    // Its caption shares the dialog's bar rather than drawing a second one.
    <ImageBlock block={asBlock} experienceId={experienceId} taskId={taskId} sharedToolbar={toolbar} {...shared} />
  ) : (
    <SoundBlock block={asBlock} experienceId={experienceId} taskId={taskId} {...shared} />
  );

  return (
    <div
      /* BOTH refs on the row. `useArmedDelete` disarms on a press outside the element it is
         given, and the bin is outside the frame — hung on the frame alone, the first press
         armed and the mousedown of the second disarmed it, so the row could never be deleted. */
      ref={(el) => { setNodeRef(el); (blockRef as React.MutableRefObject<HTMLDivElement | null>).current = el; }}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`group flex items-center gap-1 transition-opacity duration-700 ${isDragging ? 'opacity-40' : 'opacity-100'}`}
    >
      <div aria-live='assertive' aria-atomic='true' className='sr-only'>
        {isDeleteArmed ? t('blocks.actions.deleteArmed') : ''}
      </div>

      {/* THE REORDER RAIL, on the left, in the canvas's order: up, grip, down. The grip is
          `aria-hidden` and untabbable there too — it is the pointer's affordance, and the two
          buttons around it are the keyboard's, which is why they are not merely a fallback. */}
      <div className='flex shrink-0 flex-col items-center justify-center gap-0.5 self-stretch'>
        <IconButton
          size='sm'
          type='button'
          onClick={() => onMove(-1)}
          disabled={position.index === 0}
          aria-label={t('task.moveUp')}
          title={t('task.moveUp')}
        >
          <ChevronUp aria-hidden='true' className='h-4 w-4' />
        </IconButton>
        <div
          {...listeners}
          tabIndex={-1}
          aria-hidden='true'
          className='flex touch-none cursor-grab rounded active:cursor-grabbing'
        >
          <GripVertical className='h-4 w-4 text-ws-muted' />
        </div>
        <IconButton
          size='sm'
          type='button'
          onClick={() => onMove(1)}
          disabled={position.index === position.total - 1}
          aria-label={t('task.moveDown')}
          title={t('task.moveDown')}
        >
          <ChevronDown aria-hidden='true' className='h-4 w-4' />
        </IconButton>
      </div>

      {/* ONE FRAME for all three types, so the armed tint paints the same way whatever is in the
          row. `cardBorderClass` is the canvas's own expression for it — the media blocks drop
          their border under `condensed` rather than drawing a second one inside this. */}
      <div
        className={`min-w-0 flex-1 rounded-lg bg-ws-surface ${cardBorderClass({ isDeleteArmed })}`}
        /*
         * A PLAIN FIELD RELEASES THE TOOLBAR.
         *
         * The bar stays bound to whichever editor registered last, so with the caret in an
         * image's alt text it still pointed at that image's caption: pressing Bold applied it
         * to a field the teacher was not in. Alt text CANNOT be rich — it is delivered through
         * the `alt` attribute, which carries no markup — and nor can a motion description, so
         * the bar must go grey rather than offer a control that would misfire.
         *
         * By what the field IS, not by naming the three that exist today: a rich field is the
         * one tiptap marks `data-field-kind="richtext"`, so a plain box added to either block
         * later is covered without anyone remembering this. Buttons are left alone — reaching
         * for the bin should not grey the bar.
         */
        onFocusCapture={(e) => {
          const el = e.target as HTMLElement;
          if (el.closest('[data-field-kind="richtext"]')) return;
          if (el.matches('input, textarea, select')) toolbar.disconnect();
        }}
      >
        {body}
      </div>

      {/* MIDDLE-ALIGNED with the row, which `items-center` above does — the bin belongs to the
          whole block, not to its first line. */}
      <IconButton
        size='sm'
        type='button'
        variant='destructive'
        isArmed={isDeleteArmed}
        onClick={triggerDelete}
        onPointerLeave={(e) => { if (isDeleteArmed && e.pointerType !== 'touch') disarmDelete(); }}
        onBlur={() => { if (isDeleteArmed) disarmDelete(); }}
        aria-label={isDeleteArmed ? t('blocks.actions.confirmDelete') : t('blocks.actions.delete')}
      >
        <Trash2 aria-hidden='true' className='h-4 w-4' />
      </IconButton>
    </div>
  );
}
